import React from 'react';
import type { RiskLevel, SpanStatus, TraceStatus } from './types';
import { useObsTheme } from './theme';

export function Card({
  children,
  className = '',
  sticky = false,
}: {
  children: React.ReactNode;
  className?: string;
  sticky?: boolean;
}) {
  const t = useObsTheme();
  return (
    <div className={`rounded-lg border ${t.card} ${sticky ? 'sticky top-0 z-10' : ''} ${className}`}>
      {children}
    </div>
  );
}

export function CardHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
}) {
  const t = useObsTheme();
  return (
    <div className={`flex items-start justify-between gap-3 px-4 py-3 border-b ${t.cardHeaderBorder}`}>
      <div>
        <h3 className={`text-xs font-semibold ${t.title}`}>{title}</h3>
        {subtitle && <p className={`text-[10px] ${t.subtitle} mt-0.5`}>{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function SectionLabel({ children }: { children: React.ReactNode }) {
  const t = useObsTheme();
  return (
    <h2 className={`text-[11px] font-semibold uppercase tracking-wide ${t.muted} mb-2`}>{children}</h2>
  );
}

export function StatusBadge({ status }: { status: TraceStatus }) {
  const t = useObsTheme();
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium border whitespace-nowrap ${t.statusBadge[status]}`}>
      {status}
    </span>
  );
}

export function RiskBadge({ risk }: { risk: RiskLevel }) {
  const t = useObsTheme();
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium border ${t.riskBadge[risk]}`}>
      {risk}
    </span>
  );
}

const spanStatusStylesLight: Record<SpanStatus, string> = {
  success: 'text-emerald-600',
  running: 'text-blue-600',
  failed: 'text-red-600',
  blocked: 'text-violet-600',
  warning: 'text-amber-600',
};

const spanStatusStylesDark: Record<SpanStatus, string> = {
  success: 'text-emerald-400',
  running: 'text-blue-400',
  failed: 'text-red-400',
  blocked: 'text-violet-400',
  warning: 'text-amber-400',
};

export function SpanStatusDot({ status }: { status: SpanStatus }) {
  const colors: Record<SpanStatus, string> = {
    success: 'bg-emerald-500',
    running: 'bg-blue-500 animate-pulse',
    failed: 'bg-red-500',
    blocked: 'bg-violet-500',
    warning: 'bg-amber-500',
  };
  return <span className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${colors[status]}`} />;
}

export function SpanStatusText({ status }: { status: SpanStatus }) {
  const t = useObsTheme();
  const styles = t.isLight ? spanStatusStylesLight : spanStatusStylesDark;
  return <span className={`text-[10px] font-medium capitalize ${styles[status]}`}>{status}</span>;
}

export function ProviderBadge({ provider }: { provider: string }) {
  const t = useObsTheme();
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium border ${t.providerBadge[provider] ?? t.providerDefault}`}>
      {provider}
    </span>
  );
}

export function KpiCard({
  label,
  value,
  sub,
  sub2,
  trend,
  accent = 'indigo',
}: {
  label: string;
  value: string;
  sub?: string;
  sub2?: string;
  trend?: { value: string; up: boolean };
  accent?: 'indigo' | 'emerald' | 'amber' | 'red' | 'violet' | 'blue';
}) {
  const t = useObsTheme();
  const accentBorder: Record<string, string> = {
    indigo: 'border-t-indigo-500',
    emerald: 'border-t-emerald-500',
    amber: 'border-t-amber-500',
    red: 'border-t-red-500',
    violet: 'border-t-violet-500',
    blue: 'border-t-blue-500',
  };
  return (
    <div className={`rounded-lg border border-t-2 ${t.card} ${accentBorder[accent]} p-3`}>
      <p className={`text-[10px] font-medium ${t.muted} uppercase tracking-wide`}>{label}</p>
      <p className={`text-lg font-semibold ${t.title} tabular-nums mt-1`}>{value}</p>
      {sub && <p className={`text-[10px] ${t.muted} mt-0.5`}>{sub}</p>}
      {sub2 && <p className={`text-[10px] ${t.isLight ? 'text-slate-400' : 'text-zinc-600'}`}>{sub2}</p>}
      {trend && (
        <p className={`text-[10px] font-medium mt-1 ${trend.up ? 'text-emerald-500' : 'text-red-500'}`}>
          {trend.up ? '↑' : '↓'} {trend.value}
        </p>
      )}
    </div>
  );
}

export function TabBar<T extends string>({
  tabs,
  active,
  onChange,
}: {
  tabs: { id: T; label: string }[];
  active: T;
  onChange: (id: T) => void;
}) {
  const t = useObsTheme();
  return (
    <div className={`flex gap-0.5 border-b ${t.tabBarBorder} px-3`}>
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          onClick={() => onChange(tab.id)}
          className={`px-2.5 py-2 text-[10px] font-medium border-b-2 -mb-px transition-colors ${
            active === tab.id ? t.tabActive : t.tabInactive
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

export function CompactTable({
  headers,
  children,
  stickyHeader = false,
}: {
  headers: string[];
  children: React.ReactNode;
  stickyHeader?: boolean;
}) {
  const t = useObsTheme();
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-left">
        <thead className={stickyHeader ? `sticky top-0 z-[1] ${t.tableHead}` : t.tableHead}>
          <tr>
            {headers.map((h) => (
              <th
                key={h}
                className={`px-3 py-2 text-[10px] font-semibold uppercase tracking-wide ${t.tableHeadText} border-b ${t.border} whitespace-nowrap`}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className={t.tableDivide}>{children}</tbody>
      </table>
    </div>
  );
}

export function EmptyState({ title, description }: { title: string; description?: string }) {
  const t = useObsTheme();
  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
      <p className={`text-xs font-medium ${t.textSecondary}`}>{title}</p>
      {description && <p className={`text-[10px] ${t.isLight ? 'text-slate-400' : 'text-zinc-600'} mt-1 max-w-xs`}>{description}</p>}
    </div>
  );
}

export function SkeletonRow({ cols }: { cols: number }) {
  const t = useObsTheme();
  return (
    <tr>
      {Array.from({ length: cols }).map((_, i) => (
        <td key={i} className="px-3 py-2.5">
          <div className={`h-3 ${t.skeleton} rounded animate-pulse`} style={{ width: `${60 + (i % 3) * 15}%` }} />
        </td>
      ))}
    </tr>
  );
}

export function FieldRow({ label, value, mono = false }: { label: string; value: React.ReactNode; mono?: boolean }) {
  const t = useObsTheme();
  return (
    <div className={`flex justify-between gap-4 py-1.5 border-b ${t.fieldBorder} last:border-0`}>
      <span className={`text-[10px] ${t.muted} shrink-0`}>{label}</span>
      <span className={`text-[10px] ${t.text} text-right ${mono ? 'font-mono' : ''}`}>{value}</span>
    </div>
  );
}

export function WarningBadge({ children }: { children: React.ReactNode }) {
  const t = useObsTheme();
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border ${t.warningBadge}`}>
      {children}
    </span>
  );
}

export function SelectField({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
}) {
  const t = useObsTheme();
  return (
    <div className="flex flex-col gap-0.5">
      <label className={`text-[10px] font-medium ${t.muted}`}>{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`text-[11px] border rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500 ${t.select}`}
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}
